import React from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Input,
  Divider,
  Select,
  Button,
  Modal,
  Switch,
  Empty,
  Tag,
  Tooltip,
  message,
  List,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  LinkOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { MONDAY_SKU_DATABASE_BOARD_ID } from 'pages/dashboard/MondayIntegration/constants';
import { createDefaultSkuValues, generateMatrixSkuName } from '../../../../../util/sku-formatter';
import Cin7SkuMatcher from '../../../../../components/Cin7SkuMatcher/Cin7SkuMatcher';
import KountaProductLinker from '../../../../../components/KountaProductLinker/KountaProductLinker';
import ShopifyProductLinker from '../../../../../components/ShopifyProductLinker/ShopifyProductLinker';
import SkuPricingTable from './SkuPricingTable';
import {
  syncProductSkuFromMonday,
  MondaySyncNeedsSelectionError,
} from '../../../../../util/api';

// ============================================================================
// SKU CONFIGURATION
// ============================================================================

/**
 * Container types - the starting point for SKU creation flow
 */
const CONTAINER_TYPES = [
  { label: 'Can', key: 'can' },
  { label: 'Bottle', key: 'bottle' },
  { label: 'Keg', key: 'keg' },
];

/**
 * Unit types (individual container sizes) - filtered by container type
 */
const UNIT_TYPES = [
  // Cans
  { label: '250ml', key: '250', container: 'can' },
  { label: '330ml', key: '330', container: 'can' },
  { label: '375ml', key: '375', container: 'can' },
  { label: '440ml', key: '440', container: 'can' },
  { label: '500ml', key: '500', container: 'can' },

  // Bottles
  { label: '330ml', key: '330b', container: 'bottle' },
  { label: '375ml', key: '375', container: 'bottle' },
  { label: '500ml', key: '500b', container: 'bottle' },
  { label: '650ml', key: '650', container: 'bottle' },
  { label: '750ml', key: '750', container: 'bottle' },

  // Kegs - auto-set to Loose pack (1) and 1x1 case
  { label: '19.5L', key: '19500', container: 'keg' },
  { label: '20L', key: '20000', container: 'keg' },
  { label: '30L', key: '30000', container: 'keg' },
  { label: '50L', key: '50000', container: 'keg' },
];

/**
 * Pack types (how units are grouped)
 * Note: 'Loose' uses '1' (pack of 1) instead of '' to ensure proper persistence.
 * Kegs always use Loose ('1').
 */
const PACK_TYPES = [
  { label: 'Loose', key: '1' },
  { label: '4 Pack', key: '4' },
  { label: '6 Pack', key: '6' },
  { label: '10 Pack', key: '10' },
  { label: '12 Pack', key: '12' },
];

/**
 * Case types with packSize for filtering.
 * - packSize: '1' means loose (pack of 1, no intermediate pack)
 * - packSize: '4' means 4-pack, etc.
 */
const CASE_TYPES = [
  // Single unit (kegs) - 1 pack of 1 unit
  { label: '1 x Loose', key: '1x1', packSize: '1' },

  // Loose case types (packSize: '1')
  { label: '24 x Loose', key: '24x1', packSize: '1' },
  { label: '6 x Loose', key: '6x1', packSize: '1' },
  { label: '12 x Loose', key: '12x1', packSize: '1' },
  { label: '16 x Loose', key: '16x1', packSize: '1' },
  { label: '15 x Loose', key: '15x1', packSize: '1' },

  // Pack-based case types
  { label: '6 × 4 (4 Pack) (24 total)', key: '6x4', packSize: '4' },
  { label: '4 × 4 (4 Pack) (16 total)', key: '4x4', packSize: '4' },
  { label: '4 × 6 (6 Pack) (24 total)', key: '4x6', packSize: '6' },
  { label: '2 × 12 (12 Pack) (24 total)', key: '2x12', packSize: '12' },
];

/**
 * UNIT + PACK TO CASE TYPE RELATIONSHIPS
 *
 * Defines which case types are valid for specific unit + pack combinations.
 * Key format: "unitType:packType" (e.g., "330:" for 330ml loose)
 * Value: array of valid case type keys
 *
 * If a combination is not listed, defaults to filtering by packSize only.
 */
/* eslint-disable quote-props */
const UNIT_PACK_TO_CASE_RELATIONSHIPS = {
  // 330ml cans - '1' represents Loose (pack of 1)
  '330:1': ['24x1'], // 330ml loose: only 24x case
  '330:4': ['6x4', '4x4'], // 330ml 4-pack: 6x4 or 4x4 case
  '330:6': ['4x6'], // 330ml 6-pack: 4x6-pack case
  '330:12': ['2x12'], // 330ml 12-pack: 2x12-pack case

  // 440ml cans - only loose (no 4-pack or 6-pack)
  '440:1': ['24x1', '16x1', '15x1', '12x1'], // 440ml loose: 24x, 16x, 15x, or 12x case

  // 250ml cans
  '250:1': ['24x1'],
  '250:4': ['6x4'],
  '250:6': ['4x6'],
  '250:10': [], // No standard case for 10-packs
  '250:12': ['2x12'],

  // 500ml cans
  '500:1': ['24x1', '12x1'], // 500ml loose: 24x or 12x case
  '500:4': ['6x4'],
  '500:6': ['4x6'],

  // Bottles
  '330b:1': ['24x1'],
  '330b:6': ['4x6'],
  '375:1': ['24x1', '16x1', '12x1'],
  '375:4': ['6x4', '4x4'], // 375ml 4-pack: 6x4 or 4x4 case
  '375:6': ['4x6'],
  '500b:1': ['12x1'],
  '500b:6': [], // No standard case
  '650:1': ['12x1', '6x1'],
  '750:1': ['12x1', '6x1'],
  '750:6': ['4x6'], // 4 × 6-pack (750ml) case

  // Kegs - always 1x1 (single unit, no multi-pack or case)
  '19500:1': ['1x1'],
  '20000:1': ['1x1'],
  '30000:1': ['1x1'],
  '50000:1': ['1x1'],
};
/* eslint-enable quote-props */

/**
 * RELATIONSHIP CONFIGURATION
 *
 * Defines which pack types are valid for each unit type.
 * Key: unit type key (e.g., '330', '440')
 * Value: array of valid pack type keys (e.g., ['', '4', '6'])
 *
 * Use '*' as key for default (all unit types not explicitly listed)
 * Kegs are handled separately and don't use pack/case types.
 */
/* eslint-disable quote-props */
const UNIT_TO_PACK_RELATIONSHIPS = {
  // Standard cans - '1' represents Loose (pack of 1)
  '250': ['1', '4', '6', '10', '12'],
  '330': ['1', '4', '6', '12'],
  '440': ['1'], // 440ml cans: only loose
  '500': ['1', '4', '6'],

  // Bottles
  '330b': ['1', '6'],
  '375': ['1', '4', '6'],
  '500b': ['1', '6'],
  '650': ['1'],
  '750': ['1', '6'],

  // Kegs - always Loose (pack of 1)
  '19500': ['1'],
  '20000': ['1'],
  '30000': ['1'],
  '50000': ['1'],

  // Default for any unit type not listed (excluding kegs)
  '*': ['1', '4', '6', '12'],
};
/* eslint-enable quote-props */

/**
 * Quick-fill presets for new SKU creation (merge with createDefaultSkuValues).
 * Column order: Can, Keg, Bottle. Cans: loose (24×330, 15×440, 24×440), then slab
 * packs at bottom (6 Pack before 4 Pack). Label format: `×` with spaces; bottles `…ml × … bottle`;
 * kegs `…L keg`.
 */
const SKU_PRESET_COLUMNS = [
  {
    key: 'can',
    title: 'Can',
    presets: [
      {
        label: '24 × 330ml',
        values: {
          containerType: 'can', unitType: '330', packType: '1', caseType: '24x1',
        },
      },
      {
        label: '15 × 440ml',
        values: {
          containerType: 'can', unitType: '440', packType: '1', caseType: '15x1',
        },
      },
      {
        label: '24 × 440ml',
        values: {
          containerType: 'can', unitType: '440', packType: '1', caseType: '24x1',
        },
      },
      {
        label: '4 × 6 330ml (6 Pack)',
        values: {
          containerType: 'can', unitType: '330', packType: '6', caseType: '4x6',
        },
      },
      {
        label: '6 × 4 330ml (4 Pack)',
        values: {
          containerType: 'can', unitType: '330', packType: '4', caseType: '6x4',
        },
      },
      {
        label: '6 × 4 375ml (4 Pack)',
        values: {
          containerType: 'can', unitType: '375', packType: '4', caseType: '6x4',
        },
      },
      {
        label: '4 × 4 330ml (4 Pack)',
        values: {
          containerType: 'can', unitType: '330', packType: '4', caseType: '4x4',
        },
      },
      {
        label: '4 × 4 375ml (4 Pack)',
        values: {
          containerType: 'can', unitType: '375', packType: '4', caseType: '4x4',
        },
      },
    ],
  },
  {
    key: 'keg',
    title: 'Keg',
    presets: [
      {
        label: '19.5L keg',
        values: {
          containerType: 'keg', unitType: '19500', packType: '1', caseType: '1x1',
        },
      },
      {
        label: '20L keg',
        values: {
          containerType: 'keg', unitType: '20000', packType: '1', caseType: '1x1',
        },
      },
      {
        label: '30L keg',
        values: {
          containerType: 'keg', unitType: '30000', packType: '1', caseType: '1x1',
        },
      },
      {
        label: '50L keg',
        values: {
          containerType: 'keg', unitType: '50000', packType: '1', caseType: '1x1',
        },
      },
    ],
  },
  {
    key: 'bottle',
    title: 'Bottle',
    presets: [
      {
        label: '375ml × 12 bottle',
        values: {
          containerType: 'bottle', unitType: '375', packType: '1', caseType: '12x1',
        },
      },
      {
        label: '750ml × 6 bottle',
        values: {
          containerType: 'bottle', unitType: '750', packType: '1', caseType: '6x1',
        },
      },
    ],
  },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if a container type is a keg.
 * Kegs auto-set to Loose (packType='1') and 1x1 (caseType='1x1').
 * @param {string} containerType - The container type
 * @returns {boolean} True if keg
 */
const isKeg = (containerType) => containerType === 'keg';

/**
 * Get filtered unit type options based on selected container type.
 * @param {string} containerType - The selected container type (e.g., 'can', 'bottle', 'keg')
 * @returns {Array} Filtered unit type options for Select component
 */
const getFilteredUnitTypes = (containerType) => {
  if (!containerType) {
    return [];
  }

  return UNIT_TYPES
    .filter((item) => item.container === containerType)
    .map((item) => ({
      label: item.label,
      value: item.key,
    }));
};

/**
 * Get filtered pack type options based on selected unit type.
 * Returns empty array for kegs.
 * @param {string} unitType - The selected unit type (e.g., '330', '440')
 * @param {string} containerType - The container type
 * @returns {Array} Filtered pack type options for Select component
 */
const getFilteredPackTypes = (unitType, containerType) => {
  // If no unit type selected, show all pack types (empty for kegs since no unitType yet)
  if (!unitType) {
    return PACK_TYPES.map((item) => ({
      label: item.label,
      value: item.key,
    }));
  }

  // Get valid pack types for this unit type (or default)
  const validPackKeys = UNIT_TO_PACK_RELATIONSHIPS[unitType]
    || UNIT_TO_PACK_RELATIONSHIPS['*'];

  return PACK_TYPES
    .filter((item) => validPackKeys.includes(item.key))
    .map((item) => ({
      label: item.label,
      value: item.key,
    }));
};

/**
 * Get filtered case type options based on selected unit type and pack type.
 * Returns empty array for kegs.
 * @param {string} unitType - The selected unit type (e.g., '330', '440')
 * @param {string} packType - The selected pack type (e.g., '4', '6', '12', '' for loose)
 * @param {string} containerType - The container type
 * @returns {Array} Filtered case type options for Select component
 */
const getFilteredCaseTypes = (unitType, packType, containerType) => {
  // If no pack type selected, show all options
  if (packType === undefined || packType === null) {
    return CASE_TYPES.map((item) => ({
      label: item.label,
      value: item.key,
    }));
  }

  // Check for specific unit+pack combination first
  const relationshipKey = `${unitType}:${packType}`;
  const specificCaseTypes = UNIT_PACK_TO_CASE_RELATIONSHIPS[relationshipKey];

  if (specificCaseTypes !== undefined) {
    // Use the specific relationship
    return CASE_TYPES
      .filter((item) => specificCaseTypes.includes(item.key))
      .map((item) => ({
        label: item.label,
        value: item.key,
      }));
  }

  // Fall back to filtering by packSize only
  return CASE_TYPES
    .filter((item) => item.packSize === packType)
    .map((item) => ({
      label: item.label,
      value: item.key,
    }));
};

/** @see generateMatrixSkuName in util/sku-formatter */
const generateSkuName = (params) => generateMatrixSkuName(params);

/**
 * Get placeholder text for Case Type select based on current selections.
 * @param {string} unitType - Selected unit type
 * @param {string} packType - Selected pack type
 * @param {Array} caseTypeOptions - Available case type options
 * @returns {string} Placeholder text
 */
const getCaseTypePlaceholder = (unitType, packType, caseTypeOptions) => {
  if (!unitType) {
    return 'Select unit type first';
  }
  if (packType === undefined || packType === null) {
    return 'Select pack type first';
  }
  if (caseTypeOptions.length === 1) {
    return 'Auto-selected';
  }
  return 'Select case type';
};

// Legacy export for compatibility
const skuConfigs = {
  containerType: CONTAINER_TYPES,
  unitType: UNIT_TYPES,
  packType: PACK_TYPES,
  caseType: CASE_TYPES,
};

/**
 * Unified SKU Editor Component
 *
 * This component handles both viewing/editing existing SKUs and creating new ones.
 * It uses the fieldIndex to access the correct SKU in the Form.List.
 *
 * Props:
 * - fieldIndex: The index in the Form.List (required)
 * - fieldName: The field.name from Form.List (required)
 * - isNew: Whether this is a new SKU (no database key yet)
 * - onDelete: Callback to delete an existing SKU
 * - onRemove: Callback to remove a new SKU from the form (Form.List remove function)
 */
const SkuFields = ({
  fieldName,
  isNew,
  skuKey,
  productId,
  onDelete,
  onRemove,
  onSynced,
  canEditSku = true,
  savedPrices = {},
}) => {
  const form = Form.useFormInstance();
  const fieldDisabled = !canEditSku;
  const [syncingFromMonday, setSyncingFromMonday] = React.useState(false);
  const [candidateModalOpen, setCandidateModalOpen] = React.useState(false);
  const [mondayCandidates, setMondayCandidates] = React.useState([]);

  // Watch values for cascading filters (new flow: container → unit → pack → case)
  const containerType = Form.useWatch(['skus', fieldName, 'containerType']);
  const unitType = Form.useWatch(['skus', fieldName, 'unitType']);
  const packType = Form.useWatch(['skus', fieldName, 'packType']);
  const caseType = Form.useWatch(['skus', fieldName, 'caseType']);
  const currentName = Form.useWatch(['skus', fieldName, 'name']);
  const validityStatus = Form.useWatch(['skus', fieldName, 'validityStatus']);
  const validityMessage = Form.useWatch(['skus', fieldName, 'validityMessage']);
  const dearCode = Form.useWatch(['skus', fieldName, 'dearCode']);
  const skuMondayId = Form.useWatch(['skus', fieldName, 'mondayId']);
  const productMatrixId = Form.useWatch(['skus', fieldName, 'productMatrixId']);
  const parentProductName = Form.useWatch('name') || '';
  const caseTypeLabel = React.useMemo(
    () => CASE_TYPES.find((c) => c.key === caseType)?.label ?? '',
    [caseType],
  );
  const unitTypeLabel = React.useMemo(
    () => UNIT_TYPES.find((u) => u.key === unitType)?.label ?? '',
    [unitType],
  );

  // Derived state
  const isKegSku = isKeg(containerType);

  const runSyncFromMonday = React.useCallback(async (mondayItemId) => {
    if (!productId || !skuKey) return;
    setSyncingFromMonday(true);
    try {
      await syncProductSkuFromMonday(productId, skuKey, mondayItemId);
      message.success('SKU synced from Monday');
      setCandidateModalOpen(false);
      setMondayCandidates([]);
      if (onSynced) {
        await onSynced();
      }
    } catch (error) {
      if (error instanceof MondaySyncNeedsSelectionError) {
        const sorted = [...(error.candidates || [])].sort((a, b) => {
          if (a.exactKeyMatch && !b.exactKeyMatch) return -1;
          if (!a.exactKeyMatch && b.exactKeyMatch) return 1;
          return 0;
        });
        setMondayCandidates(sorted);
        setCandidateModalOpen(true);
        return;
      }
      console.error('Sync from Monday failed:', error);
    } finally {
      setSyncingFromMonday(false);
    }
  }, [productId, skuKey, onSynced]);

  const handleSyncFromMondayClick = () => {
    Modal.confirm({
      title: 'Sync from Monday',
      content: 'Pull the latest SKU data from Monday.com into Matrix? Monday values will overwrite matching fields in Matrix.',
      okText: 'Sync',
      cancelText: 'Cancel',
      onOk: () => runSyncFromMonday(),
    });
  };

  /**
   * Render validity status badge
   */
  const renderValidityBadge = () => {
    // Don't show for new SKUs (not yet saved)
    if (isNew) return null;
    // Don't show if valid or no status
    if (!validityStatus || validityStatus === 'valid') {
      return (
        <Tooltip title="SKU configuration is valid">
          <Tag color="green" icon={<CheckCircleOutlined />}>
            Valid
          </Tag>
        </Tooltip>
      );
    }

    const statusConfig = {
      invalid_combination: {
        color: 'red',
        icon: <WarningOutlined />,
        text: 'Invalid Combination',
      },
      missing_required: {
        color: 'orange',
        icon: <ExclamationCircleOutlined />,
        text: 'Missing Fields',
      },
      pending: {
        color: 'blue',
        icon: <ClockCircleOutlined />,
        text: 'Pending Validation',
      },
    };

    const config = statusConfig[validityStatus] || statusConfig.pending;

    return (
      <Tooltip title={validityMessage || config.text}>
        <Tag color={config.color} icon={config.icon}>
          {config.text}
        </Tag>
      </Tooltip>
    );
  };

  // Get filtered options based on selections
  const unitTypeOptions = getFilteredUnitTypes(containerType);
  const packTypeOptions = getFilteredPackTypes(unitType, containerType);
  const caseTypeOptions = getFilteredCaseTypes(unitType, packType, containerType);

  // Effect: Clear downstream selections when container type changes
  React.useEffect(() => {
    if (containerType) {
      // Check if current unit type is still valid for this container
      const validUnits = getFilteredUnitTypes(containerType);
      const isUnitValid = validUnits.some((u) => u.value === unitType);

      if (!isUnitValid && unitType) {
        form.setFieldValue(['skus', fieldName, 'unitType'], undefined);
        form.setFieldValue(['skus', fieldName, 'packType'], undefined);
        form.setFieldValue(['skus', fieldName, 'caseType'], undefined);
      }

      // For kegs, auto-set pack/case to Loose/1x1 (single unit format)
      if (isKeg(containerType)) {
        form.setFieldValue(['skus', fieldName, 'packType'], '1');
        form.setFieldValue(['skus', fieldName, 'caseType'], '1x1');
      }
    }
  }, [containerType, unitType, form, fieldName]);

  // Effect: Clear pack type if invalid, OR auto-select if only one option
  React.useEffect(() => {
    if (unitType) {
      const validPackKeys = UNIT_TO_PACK_RELATIONSHIPS[unitType]
        || UNIT_TO_PACK_RELATIONSHIPS['*'];

      // If current pack type is invalid, clear it
      if (packType !== undefined && packType !== null && !validPackKeys.includes(packType)) {
        form.setFieldValue(['skus', fieldName, 'packType'], undefined);
        form.setFieldValue(['skus', fieldName, 'caseType'], undefined);
      }

      // Auto-select pack type if only one option is available
      if (validPackKeys.length === 1 && packType !== validPackKeys[0]) {
        form.setFieldValue(['skus', fieldName, 'packType'], validPackKeys[0]);
      }
    }
  }, [unitType, packType, form, fieldName]);

  // Effect: Auto-select case type if only one option is available
  React.useEffect(() => {
    if (packType !== undefined && packType !== null) {
      const validCaseTypes = getFilteredCaseTypes(unitType, packType, containerType);
      if (validCaseTypes.length === 1 && caseType !== validCaseTypes[0].value) {
        form.setFieldValue(['skus', fieldName, 'caseType'], validCaseTypes[0].value);
      } else if (validCaseTypes.length === 0) {
        form.setFieldValue(['skus', fieldName, 'caseType'], undefined);
      } else if (caseType && !validCaseTypes.find((opt) => opt.value === caseType)) {
        form.setFieldValue(['skus', fieldName, 'caseType'], undefined);
      }
    }
  }, [packType, caseType, containerType, unitType, form, fieldName]);

  // Track the last auto-generated name so we keep updating until the user manually edits
  const lastAutoName = React.useRef('');

  React.useEffect(() => {
    const autoName = generateSkuName({
      productName: parentProductName,
      containerType,
      unitType,
      packType,
      caseType,
    });

    if (!autoName) return;

    const nameIsEmpty = !currentName || currentName === '';
    const nameIsAutoGenerated = currentName === lastAutoName.current;

    if (nameIsEmpty || nameIsAutoGenerated) {
      form.setFieldValue(['skus', fieldName, 'name'], autoName);
    }
    lastAutoName.current = autoName;
  }, [containerType, unitType, packType, caseType, parentProductName, currentName, form, fieldName]);

  const sectionHeadingStyle = {
    fontSize: 13,
    fontWeight: 600,
    color: 'rgba(0, 0, 0, 0.65)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: 0,
    padding: '8px 0 4px',
  };

  const twoColGrid = {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: '0 24px',
  };

  const cin7ProductHref = dearCode
    ? `https://inventory.dearsystems.com/Product#${dearCode}`
    : null;
  const mondaySkuPulseHref = skuMondayId
    ? `https://garageproject.monday.com/boards/${MONDAY_SKU_DATABASE_BOARD_ID}/pulses/${skuMondayId}`
    : null;
  const matrixProductHref = productMatrixId
    ? `/dashboard/product-database/products/${productMatrixId}/edit`
    : null;

  const labelWithExternalLink = (text, href, title) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {text}
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" title={title} aria-label={title}>
          <LinkOutlined />
        </a>
      ) : null}
    </span>
  );

  return (
    <div style={{ width: '100%', minWidth: 0 }}>
      {/* ── Header ───────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4,
      }}
      >
        <div style={{ flex: 1 }}>
          <Form.Item
            name={[fieldName, 'name']}
            disabled={fieldDisabled}
            tooltip="Auto-generated based on selections. You can edit this manually."
            rules={isNew ? [{ required: true, message: 'Please enter a SKU name' }] : undefined}
            style={{ marginBottom: 0 }}
          >
            <Input
              placeholder="Auto-generated from selections..."
              style={{ fontWeight: 600, fontSize: 16 }}
            />
          </Form.Item>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0,
        }}
        >
          <Form.Item
            name={[fieldName, 'isVenueSku']}
            disabled={fieldDisabled}
            valuePropName="checked"
            style={{ marginBottom: 0 }}
          >
            <Switch checkedChildren="Venue" unCheckedChildren="Venue" />
          </Form.Item>
          <Form.Item
            name={[fieldName, 'isLegacy']}
            disabled={fieldDisabled}
            valuePropName="checked"
            style={{ marginBottom: 0 }}
          >
            <Switch checkedChildren="Legacy" unCheckedChildren="Legacy" />
          </Form.Item>
        </div>
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16,
      }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {renderValidityBadge()}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {!isNew && canEditSku && productId && skuKey && (
            <Button
              type="default"
              icon={<SyncOutlined />}
              loading={syncingFromMonday}
              onClick={handleSyncFromMondayClick}
            >
              Sync from Monday
            </Button>
          )}
          {isNew ? (
            <Button type="default" danger icon={<DeleteOutlined />} onClick={onRemove}>
              Cancel
            </Button>
          ) : (
            <Button
              type="default"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                Modal.confirm({
                  title: 'Delete SKU',
                  content: 'Are you sure you want to delete this SKU? This action cannot be undone.',
                  onOk: () => onDelete(skuKey),
                  okText: 'Delete',
                  cancelText: 'Cancel',
                  okButtonProps: { danger: true },
                });
              }}
            >
              Delete SKU
            </Button>
          )}
        </div>
      </div>

      <Modal
        title="Select Monday SKU"
        open={candidateModalOpen}
        onCancel={() => setCandidateModalOpen(false)}
        footer={null}
        width={640}
      >
        <p style={{ marginBottom: 16, color: 'rgba(0,0,0,0.65)' }}>
          Multiple Monday SKUs match this product. Select the item to sync into Matrix.
        </p>
        <List
          dataSource={mondayCandidates}
          locale={{ emptyText: 'No matching Monday SKUs found for this product.' }}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Button
                  key="select"
                  type="link"
                  loading={syncingFromMonday}
                  onClick={() => runSyncFromMonday(item.mondayItemId)}
                >
                  Use this SKU
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={(
                  <span>
                    {item.name}
                    {item.exactKeyMatch && (
                      <Tag color="green" style={{ marginLeft: 8 }}>Matrix ID match</Tag>
                    )}
                  </span>
                )}
                description={[
                  item.formatSummary,
                  item.matrixSkuId ? `Matrix SKU ID: ${item.matrixSkuId}` : null,
                  `Monday ID: ${item.mondayItemId}`,
                ].filter(Boolean).join(' · ')}
              />
            </List.Item>
          )}
        />
      </Modal>

      {/* ── SKU Type ─────────────────────────────────────────────── */}
      <Divider orientation="left" style={{ marginTop: 0 }}>
        <span style={sectionHeadingStyle}>SKU Type</span>
      </Divider>
      <div style={twoColGrid}>
        <Form.Item
          name={[fieldName, 'containerType']}
          label="Container Type"
          labelCol={{ span: 24 }}
          disabled={fieldDisabled}
          rules={isNew ? [{ required: true, message: 'Please select container type' }] : undefined}
        >
          <Select
            placeholder="Select container type"
            options={CONTAINER_TYPES.map((item) => ({
              label: item.label,
              value: item.key,
            }))}
          />
        </Form.Item>

        <Form.Item
          name={[fieldName, 'unitType']}
          label={isKegSku ? 'Keg Size' : 'Unit Size'}
          labelCol={{ span: 24 }}
          disabled={fieldDisabled}
          rules={isNew ? [{ required: true, message: 'Please select size' }] : undefined}
        >
          <Select
            placeholder={!containerType ? 'Select container first' : 'Select size'}
            options={unitTypeOptions}
            disabled={!containerType}
          />
        </Form.Item>

        {!isKegSku && (
          <Form.Item
            name={[fieldName, 'packType']}
            label="Pack Type"
            labelCol={{ span: 24 }}
            disabled={fieldDisabled}
            rules={containerType === 'can'
              ? [{
                validator: (_, value) => {
                  if (value !== undefined && value !== null) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Please select pack type (use Loose if no pack)'));
                },
              }]
              : undefined}
          >
            <Select
              placeholder={!unitType ? 'Select size first' : 'Select pack type'}
              options={packTypeOptions}
              disabled={!unitType}
            />
          </Form.Item>
        )}
        {isKegSku && (
          <Form.Item name={[fieldName, 'packType']} hidden>
            <Input />
          </Form.Item>
        )}

        {!isKegSku && (
          <Form.Item
            name={[fieldName, 'caseType']}
            label="Case Type"
            labelCol={{ span: 24 }}
            disabled={fieldDisabled}
            rules={containerType === 'can'
              ? [{ required: true, message: 'Please select case type' }]
              : undefined}
          >
            <Select
              placeholder={getCaseTypePlaceholder(unitType, packType, caseTypeOptions)}
              options={caseTypeOptions}
              disabled={packType === undefined || packType === null}
            />
          </Form.Item>
        )}
        {isKegSku && (
          <Form.Item name={[fieldName, 'caseType']} hidden>
            <Input />
          </Form.Item>
        )}
      </div>

      {/* ── Connections ──────────────────────────────────────────── */}
      <Divider orientation="left">
        <span style={sectionHeadingStyle}>Connections</span>
      </Divider>
      <div style={twoColGrid}>
        <Form.Item
          name={[fieldName, 'cin7Sku']}
          label={labelWithExternalLink('Cin7 SKU', cin7ProductHref, 'Open product in Cin7 (DEAR)')}
          labelCol={{ span: 24 }}
          disabled={fieldDisabled}
        >
          <Cin7SkuMatcher
            parentProductName={parentProductName}
            matrixSkuName={currentName || ''}
            containerType={containerType || ''}
            unitType={unitType || ''}
            packType={packType || ''}
            caseTypeLabel={caseTypeLabel}
            unitTypeLabel={unitTypeLabel}
            onSelectMatch={(cin7Product) => {
              const sku = cin7Product?.cin7Sku ?? cin7Product?.cin7_sku ?? cin7Product?.SKU ?? '';
              const selectedDearCode = cin7Product?.cin7Id ?? cin7Product?.cin7_id ?? cin7Product?.ID ?? null;
              const skus = form.getFieldValue('skus') || [];
              const idx = Number(fieldName);
              if (!Number.isNaN(idx) && Array.isArray(skus) && skus[idx]) {
                const updated = [...skus];
                updated[idx] = { ...updated[idx], cin7Sku: sku, dearCode: selectedDearCode };
                form.setFieldsValue({ skus: updated });
              } else {
                form.setFieldValue(['skus', fieldName, 'cin7Sku'], sku);
                form.setFieldValue(['skus', fieldName, 'dearCode'], selectedDearCode);
              }
            }}
          />
        </Form.Item>

        {skuKey ? (
          <Form.Item
            label="Kounta Products"
            labelCol={{ span: 24 }}
          >
            <KountaProductLinker
              skuKey={skuKey}
              skuName={currentName || ''}
              parentProductName={parentProductName}
            />
          </Form.Item>
        ) : <div />}

        {skuKey ? (
          <Form.Item
            label="Shopify NZ"
            labelCol={{ span: 24 }}
          >
            <ShopifyProductLinker
              skuKey={skuKey}
              productMatrixId={productId || productMatrixId}
              store="GPNZ"
              disabled={fieldDisabled}
            />
          </Form.Item>
        ) : <div />}

        {skuKey ? (
          <Form.Item
            label="Shopify AU"
            labelCol={{ span: 24 }}
          >
            <ShopifyProductLinker
              skuKey={skuKey}
              productMatrixId={productId || productMatrixId}
              store="GPAU"
              disabled={fieldDisabled}
            />
          </Form.Item>
        ) : <div />}

        <Form.Item
          name={[fieldName, 'productMatrixId']}
          label={labelWithExternalLink('Parent Product ID', matrixProductHref, 'Open parent product in Matrix')}
          labelCol={{ span: 24 }}
          disabled={fieldDisabled}
        >
          <Input disabled />
        </Form.Item>

        <Form.Item
          name={[fieldName, 'mondayId']}
          label={labelWithExternalLink('Monday ID', mondaySkuPulseHref, 'Open SKU in Monday')}
          labelCol={{ span: 24 }}
          disabled={fieldDisabled}
        >
          <Input disabled />
        </Form.Item>
      </div>

      {/* Hidden fields for validity status (read-only, set by backend) */}
      <Form.Item name={[fieldName, 'validityStatus']} hidden><Input /></Form.Item>
      <Form.Item name={[fieldName, 'validityMessage']} hidden><Input /></Form.Item>
      {/* Hidden: format description and DEAR code (preserved in form, not displayed) */}
      <Form.Item name={[fieldName, 'formatDescription']} hidden><Input /></Form.Item>
      <Form.Item name={[fieldName, 'dearCode']} hidden><Input /></Form.Item>
      {/* Hidden: legacy barcode (stored in DB; not shown in UI) */}
      <Form.Item name={[fieldName, 'barcodeLegacy']} hidden><Input /></Form.Item>

      {/* ── Pricing ──────────────────────────────────────────────── */}
      <Divider orientation="left">
        <span style={sectionHeadingStyle}>Pricing</span>
      </Divider>
      <SkuPricingTable
        fieldName={fieldName}
        sku={{ containerType, packType }}
        savedPrices={savedPrices}
        disabled={fieldDisabled}
      />

      {/* ── Barcodes & Retail Codes ──────────────────────────────── */}
      <Divider orientation="left">
        <span style={sectionHeadingStyle}>Barcodes &amp; Retail Codes</span>
      </Divider>
      <div style={twoColGrid}>
        <Form.Item
          name={[fieldName, 'bnUnit']}
          label="Barcode Unit"
          labelCol={{ span: 24 }}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={[fieldName, 'bnPack']}
          label="Barcode Pack"
          labelCol={{ span: 24 }}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={[fieldName, 'bnCase']}
          label="Barcode Case"
          labelCol={{ span: 24 }}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={[fieldName, 'foodstuffsArticleNi']}
          label="Foodstuffs NI Article"
          labelCol={{ span: 24 }}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={[fieldName, 'foodstuffsArticleSi']}
          label="Foodstuffs SI Article"
          labelCol={{ span: 24 }}
        >
          <Input />
        </Form.Item>
      </div>
    </div>
  );
};

SkuFields.propTypes = {
  fieldName: PropTypes.number.isRequired,
  isNew: PropTypes.bool,
  skuKey: PropTypes.number,
  productId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onDelete: PropTypes.func,
  onRemove: PropTypes.func,
  onSynced: PropTypes.func,
  canEditSku: PropTypes.bool,
  savedPrices: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
};

SkuFields.defaultProps = {
  isNew: false,
  skuKey: null,
  productId: null,
  onDelete: () => {},
  onRemove: () => {},
  onSynced: null,
  canEditSku: true,
  savedPrices: {},
};

/**
 * Main SKU Manager Component
 *
 * Renders a single Form.List that handles both existing and new SKUs.
 * The selectedSkuKey determines which SKU to display.
 * If selectedSkuKey is 'new', shows the new SKU form or empty state.
 */
const SkuManager = ({
  selectedSkuKey,
  onDeleteSku,
  onSkuCreated,
  onSynced,
  canEditSku = true,
  savedSkuPricesRef,
}) => {
  const form = Form.useFormInstance();
  const productId = form.getFieldValue('id');
  const productMondayId = form.getFieldValue('mondayId');
  const editable = canEditSku !== false;

  return (
    <Form.List name="skus">
      {(fields, { add, remove }) => {
        // Find the field that matches the selected SKU
        const selectedField = fields.find((field) => {
          const skuData = form.getFieldValue(['skus', field.name]);
          if (selectedSkuKey === 'new') {
            // "new" tab: show SKUs without a database key
            return !skuData?.key;
          }
          // Existing SKU: match by key
          return skuData?.key === selectedSkuKey;
        });

        // Handle "new" tab
        if (selectedSkuKey === 'new') {
          if (selectedField) {
            // A new SKU is being created - show the form
            return (
              <SkuFields
                fieldName={selectedField.name}
                isNew
                onRemove={() => remove(selectedField.name)}
                canEditSku={editable}
                savedPrices={{}}
              />
            );
          }

          // No new SKU yet - show centred presets and blank add
          return (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                width: '100%',
                minHeight: 360,
                padding: '0 8px',
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16,
                  width: '100%',
                  maxWidth: 1000,
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'rgba(0, 0, 0, 0.65)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    textAlign: 'center',
                  }}
                >
                  Templates
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: 20,
                    justifyContent: 'center',
                    alignItems: 'stretch',
                  }}
                >
                  {SKU_PRESET_COLUMNS.map((column) => (
                    <div
                      key={column.key}
                      style={{
                        flex: '1 1 200px',
                        minWidth: 200,
                        maxWidth: 360,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'rgba(0, 0, 0, 0.45)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                          paddingBottom: 6,
                        }}
                      >
                        {column.title}
                      </div>
                      {column.presets.map((preset) => (
                        <Button
                          key={`${column.key}-${preset.label}`}
                          block
                          disabled={!editable}
                          onClick={() => {
                            add({
                              ...createDefaultSkuValues({ productId, productMondayId }),
                              ...preset.values,
                            });
                          }}
                        >
                          {preset.label}
                        </Button>
                      ))}
                    </div>
                  ))}
                </div>
                <Divider style={{ margin: '4px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    disabled={!editable}
                    onClick={() => {
                      add(createDefaultSkuValues({ productId, productMondayId }));
                    }}
                  >
                    Create Blank SKU
                  </Button>
                </div>
              </div>
            </div>
          );
        }

        // Handle existing SKU
        if (selectedField) {
          const skuData = form.getFieldValue(['skus', selectedField.name]);
          const skuSavedPrices = savedSkuPricesRef?.current?.[skuData?.key] || {};
          return (
            <SkuFields
              fieldName={selectedField.name}
              isNew={false}
              skuKey={skuData?.key}
              productId={productId}
              onDelete={onDeleteSku}
              onSynced={onSynced}
              canEditSku={editable}
              savedPrices={skuSavedPrices}
            />
          );
        }

        // SKU not found (shouldn't happen normally)
        return (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="SKU not found"
          />
        );
      }}
    </Form.List>
  );
};

SkuManager.propTypes = {
  selectedSkuKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onDeleteSku: PropTypes.func,
  onSkuCreated: PropTypes.func,
  onSynced: PropTypes.func,
  canEditSku: PropTypes.bool,
  savedSkuPricesRef: PropTypes.shape({ current: PropTypes.objectOf(PropTypes.any) }),
};

SkuManager.defaultProps = {
  onDeleteSku: () => {},
  onSkuCreated: () => {},
  onSynced: null,
  savedSkuPricesRef: { current: {} },
  canEditSku: true,
};

export default SkuManager;
